
/**
 * Scientifically Accurate Circadian Energy Algorithm
 * Based on SAFTE model, Two-Process Model, and Rise Science principles
 * Supports all chronotypes: Bear, Lion, Wolf, Dolphin
 */

import {
  Chronotype,
  SleepData,
  WearableData,
  EnergyLevel,
  EnergyFactors,
  EnergyState,
  CircadianParameters,
  SAFTEComponents,
  EnergyForecast,
  HormoneLevels,
  CHRONOTYPE_PROFILES,
  ENERGY_CONSTANTS,
  BaselineInput,
  WearableInput,
  UserEnergySettings
} from './types';

import { convertBaselineInputToSleepData, parseChronotype } from './utils';

// Scientific constants based on chronobiology research
const SCIENTIFIC_CONSTANTS = {
  PROCESS_S_DECAY_RATE: 0.693, // Sleep pressure decay (ln(2))
  PROCESS_C_AMPLITUDE: 0.4, // Circadian drive amplitude
  CORTISOL_HALF_LIFE: 4, // hours
  MELATONIN_HALF_LIFE: 0.5, // hours
  SLEEP_DEBT_DECAY: 0.693 / 14, // 14-day half-life
  CIRCADIAN_PERIOD: 24, // hours
  TEMPERATURE_AMPLITUDE: 0.8, // Core body temp variation
  HRV_ENERGY_CORRELATION: 0.3 // Heart rate variability impact
} as const;

/**
 * Calculate Process S (Sleep Pressure) - COMPLETELY FIXED
 */
function calculateProcessS(
  timeAwake: number,
  sleepQuality: number = 8,
  sleepDuration: number = 8
): number {
  // CRITICAL FIX: Handle negative timeAwake (person is asleep)
  if (timeAwake < 0) {
    return 0; // No sleep pressure when asleep
  }
  
  // CRITICAL FIX: Cap timeAwake at reasonable maximum (16 hours)
  const cappedTimeAwake = Math.min(timeAwake, 16);
  
  // Convert sleep quality from 1-10 scale to multiplier
  const qualityMultiplier = Math.max(0.5, (10 - sleepQuality) / 10 + 0.5);
  
  // Exponential buildup - starts slow, accelerates
  const normalizedTime = cappedTimeAwake / 16;
  const sleepPressure = Math.pow(normalizedTime, 1.2) * qualityMultiplier;
  
  // ENSURE it's always between 0 and 1
  return Math.max(0, Math.min(1, sleepPressure));
}


/**
 * Calculate Process C (Circadian Drive) component - FIXED AMPLITUDE
 */
function calculateProcessC(
  hoursSinceWake: number,
  chronotype: Chronotype,
  wakeTime: Date
): number {
  const profile = CHRONOTYPE_PROFILES[chronotype];
  
  switch (chronotype) {
    case Chronotype.BEAR:
      // Bear-specific pattern: peak at 3.5 hours after wake (10:30 AM if wake at 7 AM)
      const morningPeakHour = 3.5;
      const eveningPeakHour = 10; // 5 PM if wake at 7 AM
      
      // Morning circadian wave (primary peak)
      const morningPhase = ((hoursSinceWake - morningPeakHour) / 4) * Math.PI;
      let morningWave = (Math.cos(morningPhase) + 1) / 2;
      
      // Evening circadian wave (secondary peak - smaller)
      const eveningPhase = ((hoursSinceWake - eveningPeakHour) / 3) * Math.PI;
      let eveningWave = (Math.cos(eveningPhase) + 1) / 2 * 0.6; // 60% of morning peak
      
      // Combine waves - take the maximum
      let combinedWave = Math.max(morningWave, eveningWave);
      
      // Ensure gradual morning rise (no drop at 8 AM)
      if (hoursSinceWake >= 0 && hoursSinceWake <= 1) {
        // Very early morning - gradual awakening
        combinedWave = Math.max(combinedWave, 0.3 + (hoursSinceWake * 0.2));
      } else if (hoursSinceWake > 1 && hoursSinceWake <= 4) {
        // Morning buildup to peak - ensure no dips
        const morningProgress = (hoursSinceWake - 1) / 3;
        const minimumMorningEnergy = 0.5 + (morningProgress * 0.4);
        combinedWave = Math.max(combinedWave, minimumMorningEnergy);
      }
      
      return Math.max(0.2, Math.min(1, combinedWave));

    case Chronotype.LION:
      // Lion: Early peak at 2 hours after wake
      const lionPeakHour = 2;
      const lionPhase = ((hoursSinceWake - lionPeakHour) / 3) * Math.PI;
      let lionWave = (Math.cos(lionPhase) + 1) / 2;
      
      // Sharp decline after peak
      if (hoursSinceWake > 6) {
        lionWave *= Math.exp(-(hoursSinceWake - 6) * 0.15);
      }
      
      return Math.max(0.1, Math.min(1, lionWave));

    case Chronotype.WOLF:
      // Wolf: Late peak at 11 hours after wake
      const wolfPeakHour = 11;
      const wolfPhase = ((hoursSinceWake - wolfPeakHour) / 4) * Math.PI;
      let wolfWave = (Math.cos(wolfPhase) + 1) / 2;
      
      // Very low morning energy
      if (hoursSinceWake < 6) {
        wolfWave = 0.2 + (wolfWave * 0.3);
      }
      
      return Math.max(0.1, Math.min(1, wolfWave));

    case Chronotype.DOLPHIN:
      // Dolphin: Variable pattern with peak at 4.5 hours
      const dolphinPeakHour = 4.5;
      const dolphinPhase = ((hoursSinceWake - dolphinPeakHour) / 4) * Math.PI;
      let dolphinWave = (Math.cos(dolphinPhase) + 1) / 2;
      
      // Add irregularity
      const irregularity = Math.sin(hoursSinceWake * 0.8) * 0.15;
      dolphinWave = Math.max(0.1, dolphinWave + irregularity);
      
      return Math.max(0.1, Math.min(1, dolphinWave));

    default:
      return 0.5;
  }
}


/**
 * Calculate hormone levels - ENHANCED for all chronotypes
 */
function calculateHormoneLevels(
  hoursSinceWake: number,
  chronotype: Chronotype,
  wakeTime: Date
): HormoneLevels {
  const profile = CHRONOTYPE_PROFILES[chronotype];
  const currentTime = new Date(wakeTime.getTime() + hoursSinceWake * 60 * 60 * 1000);

  // Chronotype-specific cortisol patterns
  let cortisolPeakTime: number;
  switch (chronotype) {
    case Chronotype.LION:
      cortisolPeakTime = 0.5; // Peak 30 min after wake
      break;
    case Chronotype.WOLF:
      cortisolPeakTime = 2; // Peak 2 hours after wake
      break;
    case Chronotype.DOLPHIN:
      cortisolPeakTime = 1.5; // Peak 1.5 hours after wake
      break;
    default: // BEAR
      cortisolPeakTime = 1; // Peak 1 hour after wake
  }

  // Cortisol calculation with chronotype-specific timing
  const cortisol_peak = Math.exp(-Math.pow((hoursSinceWake - cortisolPeakTime) / 3, 2));
  const cortisol = Math.max(0.1, cortisol_peak);

  // Melatonin calculation with chronotype-specific onset
  let melatoninOnsetHours: number;
  switch (chronotype) {
    case Chronotype.LION:
      melatoninOnsetHours = 12; // Earlier onset
      break;
    case Chronotype.WOLF:
      melatoninOnsetHours = 16; // Later onset
      break;
    case Chronotype.DOLPHIN:
      melatoninOnsetHours = 15; // Variable onset
      break;
    default: // BEAR
      melatoninOnsetHours = 14; // Standard onset
  }

  const melatonin = hoursSinceWake > melatoninOnsetHours ? 
    Math.min(1, (hoursSinceWake - melatoninOnsetHours) / 3) : 0.1;

  return {
    cortisol,
    melatonin,
    timestamp: currentTime
  };
}

/**
 * Enhanced baseline energy calculation - BULLETPROOF WITH DEBUGGING
 */
export function getBaselineEnergy(
  currentTime: Date | BaselineInput,
  sleepData?: SleepData,
  chronotype: Chronotype = Chronotype.BEAR
): EnergyLevel {

  // Handle BaselineInput format - COMPLETELY REWRITTEN
  if (typeof currentTime === 'object' && !(currentTime instanceof Date)) {
    const input = currentTime as BaselineInput;
    const parsedChronotype = input.chronotype ? parseChronotype(input.chronotype) : Chronotype.BEAR;
    const wakeHour = input.wakeTimeHour || 7;
    
    
    // Generate energy array with HARDCODED sleep detection
    const energyArray: number[] = [];
    
    for (let hour = 0; hour < 24; hour++) {
      let energy: number;
      let isAsleep: boolean;
      
      // HARDCODED sleep detection for Bear (cannot be overridden)
      if (parsedChronotype === Chronotype.BEAR) {
        isAsleep = (hour >= 23 || hour < 7); // 11 PM to 6:59 AM
      } else if (parsedChronotype === Chronotype.LION) {
        isAsleep = (hour >= 21 || hour < 6); // 9 PM to 5:59 AM
      } else if (parsedChronotype === Chronotype.WOLF) {
        isAsleep = (hour >= 0 && hour < 8); // 12 AM to 7:59 AM
      } else {
        isAsleep = (hour >= 23 || hour < 7); // Default Bear pattern
      }
      
      if (isAsleep) {
        // COMPLETELY FLAT sleep energy - no randomness
        energy = 20; // Fixed value
      } else {
        // Calculate wake energy
        const hoursSinceWake = hour - wakeHour;
        const effectiveHours = Math.max(0, Math.min(16, hoursSinceWake));
        
        const processS = calculateProcessS(effectiveHours, 8);
        const processC = calculateProcessC(effectiveHours, parsedChronotype, new Date());
        
        energy = 50 + (processC * 40) - (processS * 10);
        
        // Apply chronotype adjustments
        energy = applyChronotypeSpecificAdjustments(energy, effectiveHours, parsedChronotype);
        energy = Math.max(35, Math.min(100, energy));
      }
      
      energyArray.push(energy);
    }
    
    
    return energyArray as any;
  }

  // Handle individual Date calculations (unchanged)
  const time = currentTime as Date;
  
  if (!sleepData) {
    const today = new Date(time);
    today.setHours(0, 0, 0, 0);
    
    sleepData = {
      wakeTime: new Date(today),
      bedtime: new Date(today),
      sleepDuration: 8,
      sleepDebt: 0,
      sleepQuality: 8
    };
    
    const wakeHour = chronotype === Chronotype.LION ? 6 : 
                    chronotype === Chronotype.WOLF ? 8 :
                    chronotype === Chronotype.DOLPHIN ? 6.5 : 7;
    
    const bedHour = chronotype === Chronotype.LION ? 21 :
                   chronotype === Chronotype.WOLF ? 24 :
                   chronotype === Chronotype.DOLPHIN ? 23.5 : 22;
    
    sleepData.wakeTime.setHours(Math.floor(wakeHour), (wakeHour % 1) * 60, 0, 0);
    sleepData.bedtime.setHours(Math.floor(bedHour), (bedHour % 1) * 60, 0, 0);
    
    if (time.getHours() < wakeHour) {
      sleepData.bedtime.setDate(sleepData.bedtime.getDate() - 1);
    }
  }

  const currentHour = time.getHours();
  const wakeHour = sleepData.wakeTime.getHours();
  const bedHour = sleepData.bedtime.getHours();
  
  let hoursSinceWake: number;
  if (currentHour >= wakeHour) {
    hoursSinceWake = currentHour - wakeHour;
  } else {
    hoursSinceWake = (24 - wakeHour) + currentHour;
    if (hoursSinceWake > 16) {
      hoursSinceWake = currentHour - wakeHour;
    }
  }

  // HARDCODED sleep detection (same as array version)
  let isAsleep: boolean;
  if (chronotype === Chronotype.BEAR) {
    isAsleep = (currentHour >= 23 || currentHour < 7);
  } else if (chronotype === Chronotype.LION) {
    isAsleep = (currentHour >= 21 || currentHour < 6);
  } else if (chronotype === Chronotype.WOLF) {
    isAsleep = (currentHour >= 0 && currentHour < 8);
  } else {
    isAsleep = (currentHour >= 23 || currentHour < 7);
  }

  let state: EnergyState = isAsleep ? EnergyState.ASLEEP : EnergyState.AWAKE_MODERATE;
  let effectiveHoursSinceWake = isAsleep ? 0 : Math.max(0, Math.min(16, hoursSinceWake));

  const processS = calculateProcessS(effectiveHoursSinceWake, sleepData.sleepQuality || 8);
  const processC = calculateProcessC(effectiveHoursSinceWake, chronotype, sleepData.wakeTime);
  const sleepDebtImpact = (sleepData.sleepDebt || 0) * 2;

  let baseEnergy: number;
  
  if (isAsleep) {
    baseEnergy = 20; // FIXED VALUE - no randomness
  } else {
    baseEnergy = 50 + (processC * 40) - (processS * 10) - sleepDebtImpact;
    baseEnergy = applyChronotypeSpecificAdjustments(baseEnergy, effectiveHoursSinceWake, chronotype);
    baseEnergy = Math.max(20, Math.min(100, baseEnergy));
  }


  if (!isAsleep) {
    if (baseEnergy >= 75) state = EnergyState.PEAK;
    else if (baseEnergy >= 60) state = EnergyState.AWAKE_HIGH;
    else if (baseEnergy >= 40) state = EnergyState.AWAKE_MODERATE;
    else state = EnergyState.AWAKE_LOW;
  }

  const hormones = calculateHormoneLevels(effectiveHoursSinceWake, chronotype, sleepData.wakeTime);

  const factors: EnergyFactors = {
    circadianDrive: processC,
    sleepPressure: processS,
    sleepDebt: sleepData.sleepDebt || 0,
    sleepDebtImpact,
    cortisol: hormones.cortisol,
    melatonin: hormones.melatonin
  };

  return {
    timestamp: time,
    value: baseEnergy,
    energy: baseEnergy,
    confidence: 0.85,
    factors,
    state
  };
}


/**
 * NEW HELPER: Calculate energy for a single hour (returns just the number)
 */
function calculateSingleHourEnergy(
  time: Date,
  sleepData: SleepData,
  chronotype: Chronotype
): number {
  const energyLevel = calculateSingleHourEnergyLevel(time, sleepData, chronotype);
  return energyLevel.energy;
}

/**
 * NEW HELPER: Calculate energy for a single hour (returns full EnergyLevel object)
 */
function calculateSingleHourEnergyLevel(
  time: Date,
  sleepData: SleepData,
  chronotype: Chronotype
): EnergyLevel {
  const currentHour = time.getHours();
  const wakeHour = sleepData.wakeTime.getHours();
  const bedHour = sleepData.bedtime.getHours();
  
  // Calculate hours since wake
  let hoursSinceWake: number;
  if (currentHour >= wakeHour) {
    hoursSinceWake = currentHour - wakeHour;
  } else {
    hoursSinceWake = (24 - wakeHour) + currentHour;
    if (hoursSinceWake > 16) {
      hoursSinceWake = currentHour - wakeHour; // Negative
    }
  }

  // FIXED: Reliable sleep detection for Bear chronotype
  let isAsleep = false;
  
  // Bear: Sleep from 11 PM to 7 AM
  if (chronotype === Chronotype.BEAR) {
    isAsleep = (currentHour >= 23 || currentHour < 7);
  } else if (chronotype === Chronotype.LION) {
    // Lion: Sleep from 9 PM to 6 AM
    isAsleep = (currentHour >= 21 || currentHour < 6);
  } else if (chronotype === Chronotype.WOLF) {
    // Wolf: Sleep from 12 AM to 8 AM
    isAsleep = (currentHour >= 0 && currentHour < 8);
  } else if (chronotype === Chronotype.DOLPHIN) {
    // Dolphin: Sleep from 11:30 PM to 6:30 AM
    isAsleep = (currentHour >= 23 || currentHour < 7);
  }

  let state: EnergyState = isAsleep ? EnergyState.ASLEEP : EnergyState.AWAKE_MODERATE;
  
  let effectiveHoursSinceWake = isAsleep ? 0 : Math.max(0, Math.min(16, hoursSinceWake));

  const processS = calculateProcessS(effectiveHoursSinceWake, sleepData.sleepQuality || 8);
  const processC = calculateProcessC(effectiveHoursSinceWake, chronotype, sleepData.wakeTime);
  
  const sleepDebtImpact = (sleepData.sleepDebt || 0) * 2;

  let baseEnergy: number;
  
  if (isAsleep) {
    // SLEEP ENERGY: Consistent low values
    baseEnergy = 8 + Math.random() * 7; // 8-15 range
  } else {
    // WAKE ENERGY: Full calculation
    baseEnergy = 35 + 
      (processC * 30) - 
      (processS * 20) - 
      sleepDebtImpact;

    baseEnergy = applyChronotypeSpecificAdjustments(
      baseEnergy, 
      effectiveHoursSinceWake, 
      chronotype
    );

    baseEnergy = Math.max(20, Math.min(85, baseEnergy));
  }


  if (!isAsleep) {
    if (baseEnergy >= 75) state = EnergyState.PEAK;
    else if (baseEnergy >= 60) state = EnergyState.AWAKE_HIGH;
    else if (baseEnergy >= 40) state = EnergyState.AWAKE_MODERATE;
    else state = EnergyState.AWAKE_LOW;
  }

  const hormones = calculateHormoneLevels(effectiveHoursSinceWake, chronotype, sleepData.wakeTime);

  const factors: EnergyFactors = {
    circadianDrive: processC,
    sleepPressure: processS,
    sleepDebt: sleepData.sleepDebt || 0,
    sleepDebtImpact,
    cortisol: hormones.cortisol,
    melatonin: hormones.melatonin
  };

  return {
    timestamp: time,
    value: baseEnergy,
    energy: baseEnergy,
    confidence: 0.85,
    factors,
    state
  };
}

function applyChronotypeSpecificAdjustments(
  baseEnergy: number,
  hoursSinceWake: number,
  chronotype: Chronotype
): number {
  let adjustedEnergy = baseEnergy;

  switch (chronotype) {
    case Chronotype.BEAR:
      // CORRECTED Bear pattern based on literature
      if (hoursSinceWake >= 0 && hoursSinceWake <= 1) {
        // Early morning: gentle awakening (NO DROP)
        adjustedEnergy += 2; // Small boost to prevent drop
      } else if (hoursSinceWake > 1 && hoursSinceWake <= 4) {
        // Morning energy buildup (7-11 AM) - SMOOTH RISE
        const morningBoost = (hoursSinceWake - 1) / 3 * 12; // 0-12 point boost
        adjustedEnergy += morningBoost;
      } else if (hoursSinceWake > 4 && hoursSinceWake <= 6) {
        // Peak maintenance (11 AM-1 PM)
        adjustedEnergy += 8; // Maintain high energy
      } else if (hoursSinceWake > 6 && hoursSinceWake <= 8) {
        // Post-lunch dip (1-3 PM)
        const dipIntensity = 1 - Math.abs(hoursSinceWake - 7) / 1;
        adjustedEnergy -= dipIntensity * 12;
      } else if (hoursSinceWake > 8 && hoursSinceWake <= 11) {
        // Second wind (3-6 PM)
        adjustedEnergy += 6;
      } else if (hoursSinceWake > 11) {
        // Evening decline (6-10 PM)
        adjustedEnergy -= (hoursSinceWake - 11) * 2;
      }
      break;

    case Chronotype.LION:
      // Lion adjustments (unchanged)
      if (hoursSinceWake >= 0 && hoursSinceWake <= 4) {
        adjustedEnergy += 10 * (1 - hoursSinceWake / 4);
      } else if (hoursSinceWake >= 8 && hoursSinceWake <= 10) {
        const crashIntensity = 1 - Math.abs(hoursSinceWake - 8.5) / 1.5;
        adjustedEnergy -= crashIntensity * 15;
      } else if (hoursSinceWake > 10) {
        adjustedEnergy -= (hoursSinceWake - 10) * 1.5;
      }
      break;

    case Chronotype.WOLF:
      // Wolf adjustments (unchanged)
      if (hoursSinceWake >= 0 && hoursSinceWake <= 6) {
        adjustedEnergy -= 15 * (1 - hoursSinceWake / 4);
      } else if (hoursSinceWake >= 10 && hoursSinceWake <= 14) {
        const peakIntensity = 1 - Math.abs(hoursSinceWake - 12) / 2;
        adjustedEnergy += peakIntensity * 15;
      }
      break;

    case Chronotype.DOLPHIN:
      // Dolphin adjustments (unchanged)
      if (hoursSinceWake >= 0 && hoursSinceWake <= 2) {
        adjustedEnergy -= 8 * (1 - hoursSinceWake / 2);
      } else if (hoursSinceWake >= 3 && hoursSinceWake <= 7) {
        const variability = Math.sin(hoursSinceWake * 1.2) * 4;
        adjustedEnergy += 6 + variability;
      } else if (hoursSinceWake >= 8 && hoursSinceWake <= 11) {
        const instability = Math.cos(hoursSinceWake * 1.5) * 6;
        adjustedEnergy += instability;
      }
      break;
  }

  return adjustedEnergy;
}

/**
 * Enhanced energy with wearable data integration
 */
export function getEnhancedEnergy(
  currentTime: Date,
  sleepData: SleepData,
  wearableData: WearableData,
  chronotype: Chronotype = Chronotype.BEAR
): EnergyLevel {
  const baseEnergy = getBaselineEnergy(currentTime, sleepData, chronotype);
  
  // Calculate wearable adjustments
  let wearableAdjustment = 0;
  
  if (wearableData.heartRateVariability) {
    // Higher HRV indicates better recovery
    const hrvBonus = (wearableData.heartRateVariability - 40) * 0.3;
    wearableAdjustment += Math.max(-10, Math.min(10, hrvBonus));
  }
  
  if (wearableData.stressLevel) {
    // Lower stress is better
    const stressImpact = (wearableData.stressLevel - 5) * -2;
    wearableAdjustment += Math.max(-15, Math.min(5, stressImpact));
  }
  
  if (wearableData.sleepScore) {
    // Sleep score impact
    const sleepBonus = (wearableData.sleepScore - 75) * 0.2;
    wearableAdjustment += Math.max(-10, Math.min(10, sleepBonus));
  }

  const adjustedEnergy = Math.max(0, Math.min(100, baseEnergy.energy + wearableAdjustment));
  
  return {
    ...baseEnergy,
    energy: adjustedEnergy,
    value: adjustedEnergy,
    confidence: Math.min(0.95, baseEnergy.confidence + 0.1), // Higher confidence with wearable data
    factors: {
      ...baseEnergy.factors,
      wearableFactors: {
        hrv: wearableData.heartRateVariability || 0,
        restingHeartRate: wearableData.restingHeartRate || 0,
        bodyTemperature: wearableData.bodyTemperature || 0,
        activityLevel: wearableData.activityLevel || 0
      }
    }
  };
}

/**
 * Generate comprehensive energy forecast for a day
 */
export function getEnergyForecast(
  date: Date,
  sleepData: SleepData,
  chronotype: Chronotype = Chronotype.BEAR,
  wearableData?: WearableData
): EnergyForecast {
  const hourlyPredictions: EnergyLevel[] = [];
  const peakTimes: Date[] = [];
  const dipTimes: Date[] = [];
  
  // Generate hourly predictions
  for (let hour = 0; hour < 24; hour++) {
    const currentTime = new Date(date.getTime() + hour * 60 * 60 * 1000);
    
    const energy = wearableData ? 
      getEnhancedEnergy(currentTime, sleepData, wearableData, chronotype) :
      getBaselineEnergy(currentTime, sleepData, chronotype);
    
    hourlyPredictions.push(energy);
    
    // Identify peaks and dips
    if (hour > 0 && hour < 23) {
      const prev = hourlyPredictions[hour - 1];
      const next = hour < 23 ? 
        (wearableData ? 
          getEnhancedEnergy(new Date(date.getTime() + (hour + 1) * 60 * 60 * 1000), sleepData, wearableData, chronotype) :
          getBaselineEnergy(new Date(date.getTime() + (hour + 1) * 60 * 60 * 1000), sleepData, chronotype)) :
        energy;
      
      if (energy.energy > prev.energy && energy.energy > next.energy && energy.energy >= 70) {
        peakTimes.push(currentTime);
      }
      
      if (energy.energy < prev.energy && energy.energy < next.energy && energy.energy <= 50) {
        dipTimes.push(currentTime);
      }
    }
  }

  // Calculate optimal sleep window
  const profile = CHRONOTYPE_PROFILES[chronotype];
  const optimalSleepStart = new Date(date);
  const [sleepHour, sleepMin] = profile.optimal_sleep.split(':').map(Number);
  optimalSleepStart.setHours(sleepHour, sleepMin || 0, 0, 0);
  
  const optimalSleepEnd = new Date(date);
  const [wakeHour, wakeMin] = profile.optimal_wake.split(':').map(Number);
  optimalSleepEnd.setHours(wakeHour, wakeMin || 0, 0, 0);
  if (optimalSleepEnd <= optimalSleepStart) {
    optimalSleepEnd.setDate(optimalSleepEnd.getDate() + 1);
  }

  return {
    date: date.toISOString(),
    chronotype,
    hourly_predictions: hourlyPredictions.map(hp => ({
      time: hp.timestamp.getHours().toString(),
      value: hp.energy,
      energy: hp.energy,
      timestamp: hp.timestamp,
      state: hp.state,
      factors: hp.factors,
      hour: hp.timestamp.getHours()
    })),
    peak_times: peakTimes,
    dip_times: dipTimes,
    optimal_sleep_window: [optimalSleepStart, optimalSleepEnd],
    sleep_debt_projection: sleepData.sleepDebt,
    data: hourlyPredictions.map(hp => ({
      time: hp.timestamp.getHours().toString(),
      value: hp.energy,
      energy: hp.energy,
      timestamp: hp.timestamp,
      state: hp.state,
      factors: hp.factors,
      hour: hp.timestamp.getHours()
    }))
  };
}

/**
 * Calculate optimal sleep window for chronotype
 */
export function getOptimalSleepWindow(chronotype: Chronotype): [Date, Date] {
  const profile = CHRONOTYPE_PROFILES[chronotype];
  const now = new Date();
  
  const sleepTime = new Date(now);
  const [sleepHour, sleepMin] = profile.optimal_sleep.split(':').map(Number);
  sleepTime.setHours(sleepHour, sleepMin || 0, 0, 0);
  
  const wakeTime = new Date(now);
  const [wakeHour, wakeMin] = profile.optimal_wake.split(':').map(Number);
  wakeTime.setHours(wakeHour, wakeMin || 0, 0, 0);
  
  // If wake time is before sleep time, it's the next day
  if (wakeTime <= sleepTime) {
    wakeTime.setDate(wakeTime.getDate() + 1);
  }
  
  return [sleepTime, wakeTime];
}

/**
 * Analyze energy patterns for insights
 */
export function analyzeEnergyPatterns(forecast: EnergyForecast): {
  patternType: string;
  peakQuality: 'excellent' | 'good' | 'poor';
  consistency: 'high' | 'medium' | 'low';
  recommendations: string[];
} {
  const awakeEnergy = forecast.hourly_predictions?.filter(p => p.state !== EnergyState.ASLEEP) || [];
  const energyValues = awakeEnergy.map(p => p.energy || p.value || 0);
  
  const maxEnergy = Math.max(...energyValues);
  const avgEnergy = energyValues.reduce((sum, e) => sum + e, 0) / energyValues.length;
  const variance = energyValues.reduce((sum, e) => sum + Math.pow(e - avgEnergy, 2), 0) / energyValues.length;
  
  const peakQuality = maxEnergy >= 85 ? 'excellent' : maxEnergy >= 70 ? 'good' : 'poor';
  const consistency = variance < 100 ? 'high' : variance < 300 ? 'medium' : 'low';
  
  const profile = CHRONOTYPE_PROFILES[forecast.chronotype || Chronotype.BEAR];
  const patternType = `${profile.name} chronotype pattern`;
  
  const recommendations: string[] = [];
  if (peakQuality === 'poor') {
    recommendations.push('Focus on improving sleep quality for better energy peaks');
  }
  if (consistency === 'low') {
    recommendations.push('Work on maintaining more consistent daily routines');
  }
  if ((forecast.sleep_debt_projection || 0) > 3) {
    recommendations.push('Prioritize sleep recovery to improve overall energy');
  }
  
  return {
    patternType,
    peakQuality,
    consistency,
    recommendations
  };
}

// Backward compatibility exports
export function getBaseCircadianCurve(input: BaselineInput): number[] {
  return getBaselineEnergy(input) as any;
}
